import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../redux/store";
import { hideEnquireModal } from "./reducer";
import { showLoading, hideLoading } from "../loader/reducer";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { EnquireModalBody } from "../../interfaces/connected-components/enquireModal";
import { register } from "./actions";
import { isBrowserDefined } from "../../shared/services/browserService";

/**
 * EnquireModal component that displays a registration form modal.
 * @returns {JSX.Element} EnquireModal component.
 */
const EnquireModal: React.FC = (): JSX.Element | null => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Get state from Redux using useSelector
  const { isVisible } = useSelector(
    (state: RootState) => state.enquireModal || {},
  );

  const [formData, setFormData] = useState<EnquireModalBody>({
    name: "",
    email: "",
    phone: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Add a ref to store the scroll position
  const scrollYRef = useRef(0);

  // Prevent scroll issues when modal opens/closes
  useEffect(() => {
    if (!isBrowserDefined()) return;

    if (isVisible) {
      // Save current scroll position in the ref
      scrollYRef.current = window.scrollY;

      // Apply fixed positioning without changing scroll appearance
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollYRef.current}px`;
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";
    } else {
      // Restore previous scroll position
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";

      // Use the stored ref value to restore scroll
      window.scrollTo(0, scrollYRef.current);
    }

    return () => {
      if (isBrowserDefined()) {
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.body.style.overflow = "";
      }
    };
  }, [isVisible]);

  // Reset form on modal open/close
  useEffect(() => {
    if (!isVisible) {
      setFormData({
        name: "",
        email: "",
        phone: "",
      });
      setErrors({});
      setSubmitAttempted(false);
    }
  }, [isVisible]);

  const validateEmail = (email: string): boolean => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    // Allow any phone number format that contains at least 6 digits
    const digitsOnly = phone.replace(/\D/g, "");
    return digitsOnly.length >= 6;
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Check if at least one field is filled
    if (!formData.name && !formData.email && !formData.phone) {
      newErrors.atLeastOne = t("enquireModal.errorMessages.atLeastOne");
    }

    // Email validation if provided
    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = t("enquireModal.errorMessages.email");
    }

    // Phone validation if provided
    if (formData.phone && !validatePhone(formData.phone)) {
      newErrors.phone = t("enquireModal.errorMessages.phone");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Validate email and phone on change
    if (name === "email" && value) {
      if (!validateEmail(value)) {
        setErrors((prev) => ({
          ...prev,
          email: t("enquireModal.errorMessages.email"),
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.email;
          return newErrors;
        });
      }
    } else if (name === "phone" && value) {
      if (!validatePhone(value)) {
        setErrors((prev) => ({
          ...prev,
          phone: t("enquireModal.errorMessages.phone"),
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.phone;
          return newErrors;
        });
      }
    }

    // Clear atLeastOne error if any field is filled
    if (value && errors.atLeastOne) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.atLeastOne;
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitAttempted(true);

    const isValid = validateForm();

    if (isValid) {
      dispatch(showLoading());
      const success = await register(formData);
      dispatch(hideLoading());

      if (success) {
        dispatch(hideEnquireModal());
      }
    }
  };

  const closeModal = () => {
    dispatch(hideEnquireModal());
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed top-0 left-0 w-full h-full z-[999] bg-black/40 backdrop-blur-[1px] flex justify-center items-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className="bg-white max-w-md w-full rounded-lg relative max-h-[80vh] overflow-visible flex flex-col z-[1000]"
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <button
              onClick={closeModal}
              className="absolute top-5 left-5 text-gray-500 hover:text-gray-700 z-10"
            >
              {t("enquireModal.close")}
            </button>

            <div className="text-center p-6 border-b">
              <h2 className="text-4xl font-italic text-primary mb-2 font-title italic">
                {t("enquireModal.title")}
              </h2>
              <p className="text-gray-700">{t("enquireModal.subtitle")}</p>
            </div>

            <div className="overflow-y-auto overflow-x-visible px-6 py-4 flex-grow">
              <form
                id="enquire-form"
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div>
                  <label
                    htmlFor="name"
                    className="block text-gray-700 font-medium"
                  >
                    {t("enquireModal.name")}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full border-b border-gray-300 focus:border-primary outline-none py-2"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-gray-700 font-medium"
                  >
                    {t("enquireModal.email")}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full border-b ${errors.email ? "border-red-500" : "border-gray-300"} focus:border-primary outline-none py-2`}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-gray-700 font-medium"
                  >
                    {t("enquireModal.phone")}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full border-b ${errors.phone ? "border-red-500" : "border-gray-300"} focus:border-primary outline-none py-2`}
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                  )}
                </div>

                {errors.atLeastOne && (
                  <p className="text-red-500 text-sm mt-2">
                    {errors.atLeastOne}
                  </p>
                )}
              </form>
            </div>

            <div className="flex flex-col items-center p-6 border-t bg-white">
              <button
                type="submit"
                form="enquire-form"
                className="bg-primary text-white py-3 px-16 rounded hover:bg-primary/80 transition-colors"
              >
                {t("enquireModal.submit")}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EnquireModal;
